import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { trackFilter, trackAction, trackTabChange, trackDownload } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  FileText, Download, RefreshCw, Trash2, Search, 
  User, Loader2, AlertTriangle, ChevronLeft, ChevronRight,
  Calendar, Handshake, Database, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const ResponsivasPage = () => {
  const { api, user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [responsivas, setResponsivas] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [perPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'salida', 'prestamo'
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedResponsiva, setSelectedResponsiva] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [stats, setStats] = useState({ total: 0, salidas: 0, prestamos: 0 });

  const canDelete = user?.role === 'superadmin' || user?.role === 'admin' || hasPermission('delete_responsivas');
  const canRegenerate = user?.role === 'superadmin' || user?.role === 'admin' || hasPermission('regenerate_responsivas');
  const canDownload = user?.role === 'superadmin' || user?.role === 'admin' || hasPermission('download_responsivas');
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  const fetchResponsivas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      if (search) params.append('search', search);
      if (activeTab && activeTab !== 'all') params.append('movement_type', activeTab);

      const res = await api().get(`/responsivas?${params}`);
      setResponsivas(res.data.responsivas || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      
      // Fetch stats for all types
      const [allRes, salidasRes, prestamosRes] = await Promise.all([
        api().get('/responsivas?per_page=1'),
        api().get('/responsivas?per_page=1&movement_type=salida'),
        api().get('/responsivas?per_page=1&movement_type=prestamo')
      ]);
      
      setStats({
        total: allRes.data.total || 0,
        salidas: salidasRes.data.total || 0,
        prestamos: prestamosRes.data.total || 0
      });
    } catch (err) {
      console.error('Error fetching responsivas:', err);
      toast.error('Error al cargar responsivas');
    } finally {
      setLoading(false);
    }
  }, [api, page, perPage, search, activeTab]);

  useEffect(() => {
    fetchResponsivas();
  }, [fetchResponsivas]);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const res = await api().post('/responsivas/initialize');
      toast.success(res.data.message);
      fetchResponsivas();
    } catch (err) {
      console.error('Error initializing responsivas:', err);
      toast.error('Error al inicializar responsivas');
    } finally {
      setInitializing(false);
    }
  };

  const handleDownload = async (responsiva) => {
    setDownloading(responsiva.id);
    try {
      const res = await api().get(`/movements/${responsiva.movement_id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', responsiva.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado correctamente');
      trackDownload(responsiva.filename, 'PDF Responsiva');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('Error al descargar el PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleViewPdf = async (responsiva) => {
    setViewingPdf(responsiva.id);
    try {
      const res = await api().get(`/movements/${responsiva.movement_id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Revoke after a delay to allow the new tab to load
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Error viewing PDF:', err);
      toast.error('Error al visualizar el PDF');
    } finally {
      setViewingPdf(null);
    }
  };

  const handleRegenerate = async (responsiva) => {
    setRegenerating(responsiva.id);
    trackAction('Regeneró responsiva', 'responsiva', responsiva.id);
    try {
      const res = await api().post(`/responsivas/${responsiva.movement_id}/regenerate`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', responsiva.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF regenerado y descargado');
      fetchResponsivas();
    } catch (err) {
      console.error('Error regenerating PDF:', err);
      toast.error('Error al regenerar el PDF');
    } finally {
      setRegenerating(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedResponsiva) return;
    
    try {
      await api().delete(`/responsivas/${selectedResponsiva.id}`);
      toast.success('Responsiva eliminada correctamente');
      trackAction('Eliminó responsiva', 'responsiva', selectedResponsiva.id);
      fetchResponsivas();
    } catch (err) {
      console.error('Error deleting responsiva:', err);
      toast.error('Error al eliminar la responsiva');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedResponsiva(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      'salida': { label: 'Evento', className: 'bg-blue-500 text-white' },
      'prestamo': { label: 'Préstamo', className: 'bg-amber-500 text-white' },
      'retorno': { label: 'Retorno', className: 'bg-green-500 text-white' }
    };
    const config = types[type] || { label: type, className: 'bg-gray-500 text-white' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const tabs = [
    { id: 'all', label: 'Todas', icon: FileText, count: stats.total },
    { id: 'salida', label: 'Eventos', icon: Calendar, count: stats.salidas },
    { id: 'prestamo', label: 'Préstamos', icon: Handshake, count: stats.prestamos }
  ];

  return (
    <div className="space-y-6" data-testid="responsivas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Responsivas</h1>
          <p className="text-muted-foreground">
            Historial de todas las responsivas PDF generadas
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && stats.total === 0 && (
            <Button 
              variant="outline" 
              onClick={handleInitialize} 
              disabled={initializing}
              className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
            >
              {initializing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              Inicializar desde Movimientos
            </Button>
          )}
          <Button variant="outline" onClick={fetchResponsivas} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1); trackTabChange(tab.label); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                isActive 
                  ? 'bg-primary text-white shadow-lg shadow-[#00B2A9]/20' 
                  : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
              <Badge 
                variant={isActive ? "secondary" : "outline"} 
                className={`ml-1 ${isActive ? 'bg-white/20 text-white' : ''}`}
              >
                {tab.count}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por evento, cliente, responsable..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {activeTab === 'all' ? 'Todas las Responsivas' : 
             activeTab === 'salida' ? 'Responsivas de Eventos' : 
             'Responsivas de Préstamos'}
          </CardTitle>
          <CardDescription>
            {total} responsiva{total !== 1 ? 's' : ''} encontrada{total !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : responsivas.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">No hay responsivas</h3>
              <p className="text-muted-foreground mb-4">
                {search 
                  ? 'No se encontraron responsivas con los filtros aplicados'
                  : 'Las responsivas aparecerán aquí cuando se generen desde movimientos'}
              </p>
              {isAdmin && stats.total === 0 && (
                <Button 
                  onClick={handleInitialize} 
                  disabled={initializing}
                  className="bg-primary hover:bg-[#008f88]"
                >
                  {initializing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  Inicializar desde Movimientos Existentes
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Archivo</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Evento/Razón</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Responsable</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead>Generado por</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {responsivas.map((resp) => (
                      <TableRow key={resp.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-red-500" />
                            <span className="truncate max-w-[150px]">{resp.filename}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getTypeBadge(resp.movement_type)}</TableCell>
                        <TableCell>
                          <p className="font-medium truncate max-w-[180px]">
                            {resp.event_name || '-'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="truncate max-w-[120px]">{resp.client_name || '-'}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate max-w-[100px]">{resp.responsible_name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{resp.items_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {resp.generated_by_name || '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(resp.generated_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {canDownload && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewPdf(resp)}
                                  disabled={viewingPdf === resp.id}
                                  title="Ver PDF"
                                >
                                  {viewingPdf === resp.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownload(resp)}
                                  disabled={downloading === resp.id}
                                  title="Descargar PDF"
                                >
                                  {downloading === resp.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </Button>
                              </>
                            )}
                            {canRegenerate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRegenerate(resp)}
                                disabled={regenerating === resp.id}
                                title="Regenerar PDF"
                              >
                                {regenerating === resp.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedResponsiva(resp);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((page - 1) * perPage) + 1} - {Math.min(page * perPage, total)} de {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Página {page} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Eliminar Responsiva
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar la responsiva <strong>{selectedResponsiva?.filename}</strong>?
              <br /><br />
              Esta acción no eliminará el movimiento original, solo el registro de la responsiva generada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ResponsivasPage;
